import { fetchApiData, interpolate } from '../../Utils/ApiCalls';
import { formatLocaleDate } from '../../Utils/HelperFunctions';
import {
  COMPANY_LOOKUP_ENDPOINT,
  COMPANY_SCOPES,
  SALES_TEAM_MEMBERS_ENDPOINT,
  SCOPE_RECORDS_ENDPOINT,
  SCOPE_RECORD_DETAIL_ENDPOINT,
  SCOPE_RECORD_LINK_COMPANY_ENDPOINT,
  SCOPE_RECORD_LINK_ENDPOINT,
  SCOPE_RECORD_RESEND_INVITE_ENDPOINT,
  SCOPE_RECORD_UNLINK_COMPANY_ENDPOINT
} from '../../urls';
import { fetchScopeCatalog, scopeHasSelectableElements } from '../../Utils/scopeCatalog';

export { fetchScopeCatalog, scopeHasSelectableElements };
export const dateShort = iso => formatLocaleDate(iso, { withYear: false });

const stripEmpty = obj =>
  Object.entries(obj).reduce((a, [k, v]) => {
    if (v === undefined || v === null || v === '') return a;
    a[k] = v;
    return a;
  }, {});

export const fetchScopeRecords = async ({
  linked,
  q,
  scope_form_status,
  internal_user,
  window,
  pilot_status,
  page = 1,
  limit,
  signal
} = {}) =>
  fetchApiData({
    endpoint: SCOPE_RECORDS_ENDPOINT,
    apiRequest: 'GET',
    params: stripEmpty({
      linked: linked === undefined ? undefined : String(linked),
      q,
      scope_form_status: scope_form_status ? String(scope_form_status).toLowerCase() : undefined,
      internal_user,
      window,
      pilot_status,
      page,
      limit
    }),
    signal
  });

export const fetchScopeRecord = async (id, { signal } = {}) =>
  fetchApiData({
    endpoint: interpolate(SCOPE_RECORD_DETAIL_ENDPOINT, [id]),
    apiRequest: 'GET',
    signal
  });

export const createIntakeRecord = async body =>
  fetchApiData({
    endpoint: SCOPE_RECORDS_ENDPOINT,
    apiRequest: 'POST',
    data: body
  });

export const patchScopeRecord = async (id, body) =>
  fetchApiData({
    endpoint: interpolate(SCOPE_RECORD_DETAIL_ENDPOINT, [id]),
    apiRequest: 'PATCH',
    data: body
  });

export const linkScopeRecord = async (id, companyId) =>
  fetchApiData({
    endpoint: interpolate(SCOPE_RECORD_LINK_ENDPOINT, [id]),
    apiRequest: 'POST',
    data: { company_id: companyId }
  });

export const linkCompanyToScopeRecord = async ({ scopeRecordId, companyId, cardId, reason } = {}) =>
  fetchApiData({
    endpoint: interpolate(SCOPE_RECORD_LINK_COMPANY_ENDPOINT, [scopeRecordId || cardId]),
    apiRequest: 'POST',
    data: stripEmpty({ company_id: companyId, card_id: cardId, reason })
  });

export const unlinkCompanyFromScopeRecord = async ({ scopeRecordId, reason } = {}) =>
  fetchApiData({
    endpoint: interpolate(SCOPE_RECORD_UNLINK_COMPANY_ENDPOINT, [scopeRecordId]),
    apiRequest: 'POST',
    data: stripEmpty({ reason })
  });

export const resendScopeInvite = async id =>
  fetchApiData({
    endpoint: interpolate(SCOPE_RECORD_RESEND_INVITE_ENDPOINT, [id]),
    apiRequest: 'POST'
  });

let salesTeamMembersPromise = null;

/** Cached for the session — AE/FDE list is static and fetched from several Scope Console screens. */
export const fetchSalesTeamMembers = async () => {
  if (!salesTeamMembersPromise) {
    salesTeamMembersPromise = fetchApiData({
      endpoint: SALES_TEAM_MEMBERS_ENDPOINT,
      apiRequest: 'GET'
    }).then(result => {
      if (result?.error) salesTeamMembersPromise = null;
      return result;
    });
  }
  return salesTeamMembersPromise;
};

export const fetchCompanyLookup = async ({ email, signal } = {}) =>
  fetchApiData({
    endpoint: COMPANY_LOOKUP_ENDPOINT,
    apiRequest: 'GET',
    params: stripEmpty({ email }),
    signal
  });

export const submitCompanyScopes = async ({
  scope_ids,
  element_ids,
  modifier_answers,
  notes_data,
  internal_note,
  card_id,
  actor_email
} = {}) =>
  fetchApiData({
    endpoint: COMPANY_SCOPES,
    apiRequest: 'POST',
    data: stripEmpty({
      scope_ids,
      element_ids,
      modifier_answers,
      notes_data,
      internal_note,
      card_id,
      actor_email
    })
  });

export const STATE_UI = {
  NEW: 'New',
  SENT: 'Sent',
  SUBMITTED: 'Submitted',
  REOPENED: 'Reopened',
  RESUBMITTED: 'Submitted',
  SENT_LINK_EXPIRED: 'Sent · expired',
  REOPENED_LINK_EXPIRED: 'Reopened · expired'
};

export const dayCount = iso => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86400000));
};

const agingLabels = record => {
  const state = STATE_UI[String(record.scope_form_status || record.state || '').toUpperCase()] || 'Sent';
  if (state === 'New') {
    const draftDays = dayCount(record.created_at);
    return {
      sinceSent: draftDays == null ? '' : `${draftDays}d since created`,
      inState: 'not sent to customer',
      stale: false
    };
  }
  const sentAt = record.sent_at || record.created_at;
  const days = dayCount(sentAt);
  const sinceSent = days == null ? '' : `${days}d since sent`;
  let inState = '';
  if (state === 'Sent') inState = days == null ? 'awaiting submission' : `${days}d awaiting submission`;
  else if (state === 'Submitted') inState = 'submitted';
  else if (state === 'Reopened') inState = 'reopened, awaiting submission';
  else if (state === 'Sent · expired') inState = 'link expired, resend to customer';
  else if (state === 'Reopened · expired') inState = 'reopen link expired, resend to customer';
  const expired = state === 'Sent · expired' || state === 'Reopened · expired';
  const stale = !expired && days != null && days > 7 && state !== 'Submitted';
  return { sinceSent, inState, stale };
};

export const mapRecordListRow = raw => {
  const state = STATE_UI[String(raw.scope_form_status || raw.state || '').toUpperCase()] || 'Sent';
  const { sinceSent, inState, stale } = agingLabels(raw);
  const trades = Array.isArray(raw.trades_in_scope) ? raw.trades_in_scope : [];
  const ccEmails = Array.isArray(raw.cc_emails) ? raw.cc_emails : [];
  const cc = ccEmails.length || raw.cc_count || 0;
  return {
    id: raw.id,
    linked: !!raw.company_id,
    email: raw.customer_email,
    company: raw.company_name || raw.prospect_company_name || '—',
    state,
    trades: trades.length,
    tradeList: trades.map(t => t.name),
    creator: raw.created_by?.name || '—',
    ae: raw.account_executive?.name || '—',
    fde: raw.fde?.name || raw.sales_engineer?.name || '—',
    cc,
    ccEmails,
    created: dateShort(raw.created_at),
    updated: dateShort(
      raw.updated_at || raw.current_version?.created_at || raw.submitted_at || raw.created_at
    ),
    sinceSent,
    inState,
    stale,
    pilot:
      raw.pilot_status === 'open'
        ? 'Pilot · open'
        : raw.pilot_status === 'closed_won'
          ? 'Pilot · closed won'
          : raw.pilot_status === 'closed_lost'
            ? 'Pilot · closed lost'
            : '—',
    pilotStatus: raw.pilot_status || '',
    formClosed: !!raw.form_closed,
    version: raw.current_version
      ? { id: raw.current_version.version_id, version: raw.current_version.version, actor: raw.current_version.actor_email }
      : null,
    submittedEmail:
      raw.submitted_by_email && raw.submitted_by_email !== raw.customer_email ? raw.submitted_by_email : ''
  };
};

export const mapSalesTeam = payload => ({
  ae: (payload?.AE || []).map(u => ({ label: u.name, value: u.id, email: u.email })),
  se: (payload?.SE || []).map(u => ({ label: u.name, value: u.id, email: u.email }))
});

const modifierKindLabel = type => {
  if (type === 'multi_select') return 'pick any';
  if (type === 'dropdown') return 'pick one';
  if (type === 'subjective') return 'text input';
  if (type === 'example') return 'examples';
  return type || '';
};

const mapModifier = m => ({
  id: m.id,
  title: m.title || '',
  body: m.body || '',
  hasBody: !!m.body,
  advanced: m.complexity === 'advanced',
  kindLabel: modifierKindLabel(m.modifier_type),
  modifierType: m.modifier_type,
  isInput: m.modifier_type === 'subjective',
  isExample: m.modifier_type === 'example',
  urls: Array.isArray(m.urls) ? m.urls : [],
  options: (m.answer_options || []).map(o => ({ label: o }))
});

const mapItem = scope => ({
  id: scope.scope_id,
  label: scope.scope_name,
  advanced: scope.complexity === 'advanced',
  scopeType: scope.scope_type || '',
  isAdditional: scope.scope_type === 'additional',
  isDefault: !!scope.is_default_selected,
  priceWeight: typeof scope.price_weight === 'number' ? scope.price_weight : null,
  description: scope.scope_description || '',
  link: scope.scope_link || '',
  images: Array.isArray(scope.image_urls) ? scope.image_urls : [],
  elements: (scope.elements || []).map(e => ({
    id: e.id,
    label: e.name,
    advanced: e.complexity === 'advanced'
  })),
  modifiers: (scope.modifiers || []).map(mapModifier),
  // summary:
  //   (scope.modifiers || []).length > 0
  //     ? (scope.modifiers || []).map(m => m.title).filter(Boolean).join(' · ')
  //     : 'measured as drawn'
});

const itemMatchesQuery = (item, q) => {
  if (!q) return true;
  if ((item.label || '').toLowerCase().includes(q)) return true;
  if (item.elements.some(e => (e.label || '').toLowerCase().includes(q))) return true;
  return item.modifiers.some(m => {
    if ((m.title || '').toLowerCase().includes(q)) return true;
    return m.options.some(o => (o.label || '').toLowerCase().includes(q));
  });
};

export const catalogToTradeFullView = (payload, { query = '' } = {}) => {
  const trades = payload?.trades || [];
  const q = query.trim().toLowerCase();
  return trades
    .map(t => {
      const sections = (t.sections || []).map(s => {
        const items = (s.scopes || []).filter(scopeHasSelectableElements).map(mapItem);
        const matches = q.length >= 2 ? items.filter(i => itemMatchesQuery(i, q)) : items;
        return {
          id: s.section_id,
          name: s.section_name,
          items: matches,
          totalItems: items.length
        };
      });
      const visibleSections = sections.filter(s => s.items.length);
      const totalItems = visibleSections.reduce((a, s) => a + s.totalItems, 0);
      const hitCount = visibleSections.reduce((a, s) => a + s.items.length, 0);
      return {
        id: t.trade_id,
        name: t.trade_name,
        description: t.trade_description || '',
        sections: visibleSections,
        totalItems,
        totalSections: visibleSections.length,
        hitCount: q.length >= 2 ? hitCount : 0,
        basePricePerSheet: typeof t.base_price_per_sheet === 'number' ? t.base_price_per_sheet : null,
        maxBasicPricePerSheet:
          typeof t.max_basic_price_per_sheet === 'number' ? t.max_basic_price_per_sheet : null,
        maxAdvancedPricePerSheet:
          typeof t.max_advanced_price_per_sheet === 'number' ? t.max_advanced_price_per_sheet : null,
        pricingCurveFactor: typeof t.pricing_curve_factor === 'number' ? t.pricing_curve_factor : null
      };
    })
    .filter(t => t.sections.length > 0);
};

export const catalogVolumeDiscount = payload => {
  const raw = payload?.volume_discount;
  if (!raw || typeof raw !== 'object') return [];
  return Object.entries(raw)
    .map(([minSheet, discount]) => ({ minSheet: Number(minSheet), discount: Number(discount) }))
    .filter(t => Number.isFinite(t.minSheet) && Number.isFinite(t.discount))
    .sort((a, b) => a.minSheet - b.minSheet);
};

export const catalogIsStaffView = payload => Array.isArray(payload?.trades) && !!payload?.volume_discount;

export const catalogToTradeCards = payload => {
  const trades = payload?.trades || [];
  return trades
    .map(t => {
      let itemCount = 0;
      let elementCount = 0;
      let sectionCount = 0;
      (t.sections || []).forEach(s => {
        const scopes = (s.scopes || []).filter(scopeHasSelectableElements);
        if (!scopes.length) return;
        sectionCount += 1;
        scopes.forEach(sc => {
          itemCount += 1;
          elementCount += (sc.elements || []).length;
        });
      });
      return {
        id: t.trade_id,
        name: t.trade_name,
        description: t.trade_description || '',
        sections: sectionCount,
        items: itemCount,
        elements: elementCount
      };
    })
    .filter(t => t.sections > 0);
};

// Flatten a single trade (from GET /api/dashboard/get_scopes/) into on-behalf items —
// scope-level rows the internal user can tick to fill out the customer form.
export const catalogTradeToItems = (catalogPayload, tradeId) => {
  const trade = (catalogPayload?.trades || []).find(t => t.trade_id === tradeId);
  if (!trade) return [];
  const rows = [];
  (trade.sections || []).forEach(section => {
    (section.scopes || []).forEach(scope => {
      const elementCount = (scope.elements || []).length;
      const modifierCount = (scope.modifiers || []).length;
      rows.push({
        id: scope.scope_id,
        label: scope.scope_name,
        sectionName: section.section_name,
        advanced: scope.complexity === 'advanced',
        subline: `${elementCount} elements · ${
          modifierCount ? `${modifierCount} question${modifierCount > 1 ? 's' : ''}` : 'no questions'
        }`
      });
    });
  });
  return rows.slice(0, 8);
};

export const parseFieldErrors = errorPayload => {
  if (!errorPayload) return {};
  if (typeof errorPayload === 'string') return { _detail: errorPayload };
  if (errorPayload.detail) return { _detail: errorPayload.detail };
  return Object.entries(errorPayload).reduce((a, [k, v]) => {
    a[k] = Array.isArray(v) ? v.join(', ') : String(v);
    return a;
  }, {});
};
